import { useMemo } from 'react'

import Box from '@ilg-totallymoney/ui/components/Box'
import Hero from '@ilg-totallymoney/ui/components/Hero'
import LinkButton from '@ilg-totallymoney/ui/components/LinkButton'
// @ts-ignore
import TrustBox from '@ilg-totallymoney/ui/components/TrustBox'
import { LoanProductCategory } from '@mediaingenuity/totallymoney.types'

import useAppQueryState from '@hooks/useAppQueryState'
import useLogEvents from '@hooks/useLogEvents'

import carFinanceHeroImage from '@images/car-finance-hero.png'
import loansHeroImage from '@images/loans-hero.png'

import { getAppConfig } from '@utils/getAppConfig'
import { getRedirectAccountUrl } from '@utils/getRedirectAccountUrl'

interface UnscoredPageHeaderProps {
    activeCategoryId: LoanProductCategory | null
}

function getCategoryTitle(categoryId: LoanProductCategory | null) {
    switch (categoryId) {
        case 'PoorCredit':
            return 'Compare loans for bad credit'
        case 'CarFinance':
            return 'Compare car finance loans'
        case 'Secured':
            return 'Lock in a low rate homeowner loan'
        case 'Guarantor':
            return 'Compare guarantor loans'
        case 'AlternativeProducts':
            return 'Compare alternative products'
        case 'PersonalLoan':
        default:
            return 'Find your perfect loan'
    }
}

const homeownerChecklist = [
    'You could be eligible, even if you don’t have a high credit score',
    'Get a decision in seconds, so you can apply with confidence',
    'Checking won’t harm your credit score',
]

const genericCheckList = [
    'Boost your chances of approval',
    'No impact to your credit rating',
    'Get an instant response',
]

function getCategoryIllustration(categoryKey: string) {
    if (categoryKey === 'CarFinance') {
        return carFinanceHeroImage?.src
    }
    return loansHeroImage?.src
}

const UnscoredPageHeader = ({ activeCategoryId }: UnscoredPageHeaderProps) => {
    const { appQueryState } = useAppQueryState()
    const illustrationSrc = getCategoryIllustration(activeCategoryId!)
    const logEvents = useLogEvents()
    const appConfig = getAppConfig()

    const formUrl = useMemo(() => {
        const data = {
            queryObject: {
                category: appQueryState.category,
                loanAmount: Number(appQueryState.loanAmount),
                loanTerm: Number(appQueryState.loanTerm),
                client: appConfig.NAME_WHITELABEL,
            },
        }
        return getRedirectAccountUrl(data)
    }, [activeCategoryId])

    const onCheckEligibilityClick = (
        event: React.SyntheticEvent<Element, Event>
    ) => {
        event.preventDefault()

        logEvents.logCheckEligibilityEvent({
            category: appQueryState.categoryId!,
            loanAmount: Number(appQueryState.loanAmount!),
            loanTerm: Number(appQueryState.loanTerm!),
            origin: 'header',
        })

        window.location.href = formUrl
    }

    return (
        <>
            <Hero
                headline={getCategoryTitle(activeCategoryId)}
                subtitle="Compare loans and see the lowest rate available to you"
                checkList={
                    activeCategoryId === 'Secured'
                        ? homeownerChecklist
                        : genericCheckList
                }
                illustration={illustrationSrc}
                illustrationSrcset={`${illustrationSrc} 600w, ${illustrationSrc} 1024w`}
                illustrationSizes="(min-width: 960px) 720px, 100vw"
            >
                <LinkButton
                    variant="primaryDark"
                    data-gtm-event="loans-formOpen-header"
                    href={formUrl}
                    text="Check my eligibility"
                    onClick={onCheckEligibilityClick}
                />
            </Hero>

            <Box pt={['space20', 0]} pb="space30" backgroundColor="white">
                <TrustBox widgetId="trustpilot-widget" />
            </Box>
        </>
    )
}

export default UnscoredPageHeader
